// src/components/TransactionForm.jsx
// ---------------------------------------------------------
// The add/edit transaction form, including the AI bulk-parse panel.
// ---------------------------------------------------------

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  PlusCircle,
  Pencil,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { callGeminiProxy } from "../lib/gemini";
import {
  DEFAULT_TRANSACTION_TYPES,
  DEFAULT_CATEGORIES,
  normalizeTransactionTypeKey,
} from "../lib/constants";

const emptyForm = {
  date: new Date().toISOString().split("T")[0],
  type: "expense",
  category: "Substructure",
  payee: "",
  amount: "",
  notes: "",
};

export default function TransactionForm({
  onAdd,
  onUpdate,
  editingTx,
  onCancelEdit,
  transactionTypes = DEFAULT_TRANSACTION_TYPES,
  categories = DEFAULT_CATEGORIES,
  onCreateType,
  onCreateCategory,
}) {
  const [form, setForm] = useState(editingTx || emptyForm);
  const types = transactionTypes;
  const cats = categories;

  const [newType, setNewType] = useState({
    adding: false,
    value: "",
    behavior: "outflow",
  });
  const [newCat, setNewCat] = useState({ adding: false, value: "" });
  const [newCatError, setNewCatError] = useState("");

  const [showAI, setShowAI] = useState(false);
  const [invoiceText, setInvoiceText] = useState("");
  const [file, setFile] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parseMsg, setParseMsg] = useState({ type: "", text: "" });
  const [parsedItems, setParsedItems] = useState([]);
  const [parsedFullscreen, setParsedFullscreen] = useState(false);

  const [parsedSelectedIds, setParsedSelectedIds] = useState([]);
  const [parsedFilterType, setParsedFilterType] = useState("all");
  const [parsedKeyword, setParsedKeyword] = useState("");
  const [parsedSortBy, setParsedSortBy] = useState("date-desc");

  // Tracks the active countdown interval so it can be cleared on re-parse or unmount.
  const countdownRef = useRef(null);

  // Clear any running countdown when the component unmounts.
  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  useEffect(() => {
    if (!editingTx) return;
    const normalizedType = normalizeType(editingTx.type, types);
    setForm({
      ...editingTx,
      type: normalizedType,
      category: editingTx.category || cats[normalizedType]?.[0] || "General",
      amount: String(editingTx.amount ?? ""),
    });
  }, [editingTx, cats, types]);

  useEffect(() => {
    if (editingTx) return;
    setForm((prev) => {
      const normalizedType = normalizeType(prev.type, types);
      const currentTypeExists = !!types[normalizedType];
      const safeType = currentTypeExists
        ? normalizedType
        : Object.keys(types)[0] || "expense";
      const typeCategories = cats[safeType] || [];
      const safeCategory =
        prev.category && typeCategories.includes(prev.category)
          ? prev.category
          : typeCategories[0] || "General";
      return { ...prev, type: safeType, category: safeCategory };
    });
  }, [editingTx, cats, types]);

  useEffect(() => {
    if (!parsedItems.length) setParsedFullscreen(false);
  }, [parsedItems.length]);

  useEffect(() => {
    setParsedSelectedIds((prev) =>
      prev.filter((id) => parsedItems.some((item) => item.id === id)),
    );
  }, [parsedItems]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "type") next.category = cats[value]?.[0] || "General";
      return next;
    });
    if (e.target.name === "type") {
      setNewCat({ adding: false, value: "" });
      setNewCatError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.payee) return;

    const typeKey = normalizeType(form.type, types);
    const data = {
      ...form,
      type: typeKey,
      category: form.category || cats[typeKey]?.[0] || "General",
      amount: Math.abs(parseFloat(form.amount)),
    };

    if (editingTx) {
      await onUpdate(editingTx.id, data);
      onCancelEdit();
    } else {
      await onAdd(data);
      setForm((prev) => ({ ...prev, payee: "", amount: "", notes: "" }));
    }
  };

  const handleAddType = async () => {
    const v = newType.value.trim();
    if (!v) return;
    const key = v.toLowerCase().replace(/[^a-z0-9]/g, "_");
    await onCreateType?.(key, { label: v, type: newType.behavior });
    await onCreateCategory?.(key, "General");
    await onCreateCategory?.(key, "Other");
    setForm((prev) => ({ ...prev, type: key, category: "General" }));
    setNewType({ adding: false, value: "", behavior: "outflow" });
  };

  const handleAddCat = async () => {
    const v = newCat.value.trim();
    if (!v) return;
    const typeConfig = types[form.type] || DEFAULT_TRANSACTION_TYPES.expense;
    const impliedDirection = detectCategoryDirection(v);
    if (
      impliedDirection &&
      !isCategoryDirectionCompatible(impliedDirection, typeConfig.type)
    ) {
      setNewCatError(
        `Cannot add "${v}" to "${typeConfig.label || form.type}". This category name implies ${formatDirectionLabel(impliedDirection)} while this type is ${formatDirectionLabel(typeConfig.type)}.`,
      );
      return;
    }
    try {
      await onCreateCategory?.(form.type, v);
      setForm((prev) => ({ ...prev, category: v }));
      setNewCat({ adding: false, value: "" });
      setNewCatError("");
    } catch (err) {
      setNewCatError(
        err?.message || "Could not add category for the selected transaction type.",
      );
    }
  };

  const handleSmartParse = async () => {
    if (!invoiceText.trim() && !file) return;

    // Cancel any existing countdown before starting a new request.
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    setIsParsing(true);
    setParseMsg({ type: "", text: "" });

    const allCats = Array.from(
      new Set(Object.values(cats || {}).flat().filter(Boolean)),
    );
    const system = `Extract ALL transaction details. Return ONLY a valid JSON ARRAY:\n[{"date":"YYYY-MM-DD","payee":"string","amount":number,"notes":"string","category":"closest from: ${allCats.join(", ")}","type":"expense or income or liability"}]`;

    try {
      const raw = await callGeminiProxy(
        invoiceText.trim()
          ? `Text:\n${invoiceText}`
          : "Extract from attached document.",
        system,
        true,
        file,
      );

      const parsed = JSON.parse(
        raw.replace(/```json/gi, "").replace(/```/g, "").trim(),
      );
      const rows = Array.isArray(parsed) ? parsed : parsed.transactions || [parsed];

      if (!rows.length) throw new Error("No transactions found in the document.");

      const normalized = rows
        .map((row, idx) => {
          const typeKey = normalizeType(row.type, types);
          const category =
            (typeof row.category === "string" && row.category.trim()) ||
            cats[typeKey]?.[0] ||
            "Other";
          return {
            id: `${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 8)}`,
            date: normalizeDate(row.date),
            type: typeKey,
            category,
            payee: typeof row.payee === "string" ? row.payee : "",
            amount: normalizeAmount(row.amount),
            notes: typeof row.notes === "string" ? row.notes : "Auto-extracted",
          };
        })
        .filter((item) => item.payee || item.amount);

      if (!normalized.length) throw new Error("No usable line items were found.");

      setParsedItems(normalized);
      setParsedSelectedIds([]);
      setParsedFilterType("all");
      setParsedKeyword("");
      setParsedSortBy("date-desc");

      setInvoiceText("");
      setFile(null);
      setParseMsg({
        type: "success",
        text: `Parsed ${normalized.length} line item(s). Select and add in bulk, or add one by one.`,
      });
    } catch (err) {
      // Show a countdown for rate-limit and service-unavailable errors so users
      // know how long to wait before clicking again.
      const countdownSeconds = err.status === 429 ? 60 : err.status === 503 ? 15 : 0;
      const baseMsg =
        err.status === 429
          ? "Rate limit reached."
          : err.status === 503
          ? "Service temporarily unavailable."
          : err.message || "Extraction failed.";

      if (countdownSeconds > 0) {
        let remaining = countdownSeconds;
        const tick = () => {
          setParseMsg({
            type: "error",
            text: `${baseMsg} Please wait ${remaining}s before retrying.`,
          });
          if (remaining <= 0) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
            setParseMsg({
              type: "error",
              text: `${baseMsg} You can now click "Extract & Parse" to try again.`,
            });
          }
          remaining -= 1;
        };
        tick();
        countdownRef.current = setInterval(tick, 1000);
      } else {
        setParseMsg({ type: "error", text: baseMsg });
      }
    } finally {
      setIsParsing(false);
    }
  };

  const handleParsedItemChange = (id, field, value) => {
    setParsedItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        if (field === "type") {
          return {
            ...item,
            type: value,
            category: cats[value]?.[0] || "General",
          };
        }
        return { ...item, [field]: value };
      }),
    );
  };

  const removeParsedItem = (id) => {
    setParsedItems((prev) => prev.filter((item) => item.id !== id));
    setParsedSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
  };

  const clearParsedItems = () => {
    setParsedItems([]);
    setParsedSelectedIds([]);
    setParseMsg({ type: "success", text: "All parsed items cleared." });
  };

  const normalizeKeyword = parsedKeyword.trim().toLowerCase();

  const visibleParsedItems = useMemo(() => {
    let items = [...parsedItems];

    if (parsedFilterType !== "all") {
      items = items.filter((item) => item.type === parsedFilterType);
    }

    if (normalizeKeyword) {
      items = items.filter((item) =>
        [
          item.payee,
          item.category,
          item.notes,
          item.date,
          types[item.type]?.label || item.type,
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalizeKeyword),
      );
    }

    items.sort((a, b) => {
      if (parsedSortBy === "date-desc") return new Date(b.date) - new Date(a.date);
      if (parsedSortBy === "date-asc") return new Date(a.date) - new Date(b.date);
      if (parsedSortBy === "amount-desc") return parseFloat(b.amount || 0) - parseFloat(a.amount || 0);
      if (parsedSortBy === "amount-asc") return parseFloat(a.amount || 0) - parseFloat(b.amount || 0);
      if (parsedSortBy === "payee-asc") return String(a.payee || "").localeCompare(String(b.payee || ""));
      if (parsedSortBy === "payee-desc") return String(b.payee || "").localeCompare(String(a.payee || ""));
      return 0;
    });

    return items;
  }, [normalizeKeyword, parsedFilterType, parsedItems, parsedSortBy, types]);

  const visibleParsedIds = useMemo(
    () => visibleParsedItems.map((item) => item.id),
    [visibleParsedItems],
  );

  const allVisibleParsedSelected =
    !!visibleParsedIds.length &&
    visibleParsedIds.every((id) => parsedSelectedIds.includes(id));

  const toggleParsedSelection = (id) => {
    setParsedSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id],
    );
  };

  const toggleSelectAllVisibleParsed = () => {
    setParsedSelectedIds((prev) => {
      if (allVisibleParsedSelected) {
        return prev.filter((id) => !visibleParsedIds.includes(id));
      }
      const merged = new Set([...prev, ...visibleParsedIds]);
      return Array.from(merged);
    });
  };

  const removeSelectedParsedItems = () => {
    if (!parsedSelectedIds.length) return;
    const selected = new Set(parsedSelectedIds);
    setParsedItems((prev) => prev.filter((item) => !selected.has(item.id)));
    setParsedSelectedIds([]);
    setParseMsg({ type: "success", text: "Selected parsed item(s) removed." });
  };

  const buildParsedPayload = (item) => {
    const amount = parseFloat(item.amount);
    if (!item.payee?.trim() || Number.isNaN(amount) || amount <= 0) return null;

    return {
      date: item.date || new Date().toISOString().split("T")[0],
      type: normalizeType(item.type || "expense", types),
      category: item.category || "Other",
      payee: item.payee.trim(),
      amount,
      notes: item.notes || "",
    };
  };

  const addParsedItemsByIds = async (ids, source = "selected") => {
    const targetIds = Array.from(new Set(ids));
    if (!targetIds.length) return;

    const itemsToAdd = parsedItems.filter((item) => targetIds.includes(item.id));
    const valid = [];
    let invalidCount = 0;

    itemsToAdd.forEach((item) => {
      const payload = buildParsedPayload(item);
      if (payload) valid.push({ id: item.id, payload });
      else invalidCount += 1;
    });

    if (!valid.length) {
      setParseMsg({
        type: "error",
        text: "No valid items to add. Each item needs a payee and amount greater than zero.",
      });
      return;
    }

    const results = await Promise.allSettled(
      valid.map(({ payload }) => onAdd(payload)),
    );
    const successfulIds = valid
      .map((entry, idx) => ({ id: entry.id, ok: results[idx].status === "fulfilled" }))
      .filter((entry) => entry.ok)
      .map((entry) => entry.id);
    const failedCount = results.filter((result) => result.status === "rejected").length;

    if (successfulIds.length) {
      const added = new Set(successfulIds);
      setParsedItems((prev) => prev.filter((item) => !added.has(item.id)));
      setParsedSelectedIds((prev) => prev.filter((id) => !added.has(id)));
    }

    const actionLabel =
      source === "all" ? "item(s) from all parsed rows" : "selected item(s)";
    const parts = [];
    if (successfulIds.length) parts.push(`Added ${successfulIds.length} ${actionLabel}.`);
    if (invalidCount) parts.push(`${invalidCount} item(s) skipped (invalid payee/amount).`);
    if (failedCount) parts.push(`${failedCount} item(s) failed to save.`);

    setParseMsg({
      type: failedCount ? "error" : "success",
      text: parts.join(" "),
    });
  };

  const addParsedItem = async (item) => {
    await addParsedItemsByIds([item.id], "selected");
  };

  const renderParsedRows = (heightClass) => (
    <>
      <div className="p-3 border-b border-amber-100 bg-amber-50/40 space-y-2.5">
        <div className="flex flex-col md:flex-row gap-2 md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-2 text-[11px] font-bold text-amber-900 uppercase tracking-wide">
              <input
                type="checkbox"
                checked={allVisibleParsedSelected}
                onChange={toggleSelectAllVisibleParsed}
                className="rounded border-amber-300"
              />
              Select All Visible
            </label>
            <span className="text-[11px] font-semibold text-amber-800">
              Selected: {parsedSelectedIds.length}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => addParsedItemsByIds(parsedSelectedIds, "selected")}
              disabled={!parsedSelectedIds.length}
              className="px-2.5 py-1.5 text-[11px] font-bold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              Add Selected
            </button>
            <button
              type="button"
              onClick={() =>
                addParsedItemsByIds(
                  parsedItems.map((item) => item.id),
                  "all",
                )
              }
              disabled={!parsedItems.length}
              className="px-2.5 py-1.5 text-[11px] font-bold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              Add All
            </button>
            <button
              type="button"
              onClick={removeSelectedParsedItems}
              disabled={!parsedSelectedIds.length}
              className="px-2.5 py-1.5 text-[11px] font-bold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
            >
              Remove Selected
            </button>
            <button
              type="button"
              onClick={clearParsedItems}
              disabled={!parsedItems.length}
              className="px-2.5 py-1.5 text-[11px] font-bold rounded-lg bg-rose-100 text-rose-700 hover:bg-rose-200 disabled:opacity-50"
            >
              Clear All
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input
            type="text"
            value={parsedKeyword}
            onChange={(e) => setParsedKeyword(e.target.value)}
            placeholder="Keyword filter (payee, notes, category...)"
            className="md:col-span-2 text-xs p-2 border border-amber-200 rounded-lg bg-white"
          />
          <select
            value={parsedFilterType}
            onChange={(e) => setParsedFilterType(e.target.value)}
            className="text-xs p-2 border border-amber-200 rounded-lg bg-white"
          >
            <option value="all">All Types</option>
            {Object.entries(types).map(([k, c]) => (
              <option key={k} value={k}>
                {c.label}
              </option>
            ))}
          </select>
          <select
            value={parsedSortBy}
            onChange={(e) => setParsedSortBy(e.target.value)}
            className="text-xs p-2 border border-amber-200 rounded-lg bg-white"
          >
            <option value="date-desc">Date (Newest)</option>
            <option value="date-asc">Date (Oldest)</option>
            <option value="amount-desc">Amount (High-Low)</option>
            <option value="amount-asc">Amount (Low-High)</option>
            <option value="payee-asc">Payee (A-Z)</option>
            <option value="payee-desc">Payee (Z-A)</option>
          </select>
        </div>
      </div>

      <div className={`${heightClass} overflow-auto divide-y divide-slate-100`}>
        {visibleParsedItems.map((item) => (
          <div
            key={item.id}
            className="p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-end"
          >
            <div className="md:col-span-1">
              <label className="block text-[10px] uppercase text-slate-500 font-bold mb-1">
                Select
              </label>
              <div className="h-9 flex items-center justify-center border border-slate-200 rounded-lg bg-slate-50">
                <input
                  type="checkbox"
                  checked={parsedSelectedIds.includes(item.id)}
                  onChange={() => toggleParsedSelection(item.id)}
                  className="rounded border-slate-300"
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase text-slate-500 font-bold mb-1">
                Date
              </label>
              <input
                type="date"
                value={item.date}
                onChange={(e) =>
                  handleParsedItemChange(item.id, "date", e.target.value)
                }
                className="w-full text-xs p-2 border border-slate-200 rounded-lg bg-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase text-slate-500 font-bold mb-1">
                Type
              </label>
              <select
                value={item.type}
                onChange={(e) =>
                  handleParsedItemChange(item.id, "type", e.target.value)
                }
                className="w-full text-xs p-2 border border-slate-200 rounded-lg bg-white"
              >
                {Object.entries(types).map(([k, c]) => (
                  <option key={k} value={k}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase text-slate-500 font-bold mb-1">
                Category
              </label>
              <input
                type="text"
                value={item.category}
                onChange={(e) =>
                  handleParsedItemChange(item.id, "category", e.target.value)
                }
                className="w-full text-xs p-2 border border-slate-200 rounded-lg bg-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase text-slate-500 font-bold mb-1">
                Payee
              </label>
              <input
                type="text"
                value={item.payee}
                onChange={(e) =>
                  handleParsedItemChange(item.id, "payee", e.target.value)
                }
                className="w-full text-xs p-2 border border-slate-200 rounded-lg bg-white"
              />
            </div>
            <div className="md:col-span-1">
              <label className="block text-[10px] uppercase text-slate-500 font-bold mb-1">
                Amount
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={item.amount}
                onChange={(e) =>
                  handleParsedItemChange(item.id, "amount", e.target.value)
                }
                className="w-full text-xs p-2 border border-slate-200 rounded-lg bg-white"
              />
            </div>
            <div className="md:col-span-1">
              <label className="block text-[10px] uppercase text-slate-500 font-bold mb-1">
                Notes
              </label>
              <input
                type="text"
                value={item.notes}
                onChange={(e) =>
                  handleParsedItemChange(item.id, "notes", e.target.value)
                }
                className="w-full text-xs p-2 border border-slate-200 rounded-lg bg-white"
              />
            </div>
            <div className="md:col-span-1 flex md:flex-col gap-2">
              <button
                type="button"
                onClick={() => addParsedItem(item)}
                className="flex-1 md:w-full px-2 py-2 text-xs font-bold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => removeParsedItem(item.id)}
                className="flex-1 md:w-full px-2 py-2 text-xs font-bold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        {!visibleParsedItems.length && (
          <div className="p-6 text-center text-xs text-slate-500 bg-slate-50">
            No parsed items match your current type/keyword filter.
          </div>
        )}
      </div>
    </>
  );

  const isEditing = !!editingTx;

  return (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-bold flex items-center gap-2 text-slate-800">
          {isEditing ? (
            <>
              <Pencil className="w-5 h-5 text-amber-500" /> Edit Transaction
            </>
          ) : (
            <>
              <PlusCircle className="w-5 h-5 text-indigo-500" /> New Transaction
            </>
          )}
        </h2>
        {!isEditing && (
          <button
            onClick={() => setShowAI(!showAI)}
            className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 ${
              showAI
                ? "bg-indigo-100 text-indigo-700"
                : "bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200/50"
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" /> Auto-Fill
          </button>
        )}
      </div>

      {showAI && !isEditing && (
        <div className="mb-6 p-4 bg-amber-50/50 border border-amber-200 rounded-xl space-y-4">
          <label className="text-xs font-bold text-amber-900 flex items-center gap-1.5 uppercase tracking-wide">
            <Sparkles className="w-3.5 h-3.5" /> AI Bulk Upload & Parse
          </label>
          <textarea
            value={invoiceText}
            onChange={(e) => setInvoiceText(e.target.value)}
            placeholder="Paste receipt, bank alert, or statement text here..."
            className="w-full text-sm p-3 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-400 outline-none h-24 resize-none bg-white placeholder:text-amber-800/40"
          />
          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700/80">
              Or attach document (CSV, PDF, Image, XLSX, DOC)
            </span>
            <input
              type="file"
              accept=".csv,.xls,.xlsx,.doc,.docx,image/*,application/pdf"
              onChange={(e) => setFile(e.target.files[0])}
              className="text-xs text-amber-900 file:mr-3 file:py-1.5 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-amber-200 file:text-amber-800 hover:file:bg-amber-300 w-full bg-white border border-amber-200 rounded-lg p-1"
            />
          </div>

          {parseMsg.text && (
            <div
              className={`text-xs font-bold p-2.5 rounded-lg flex items-center gap-2 ${
                parseMsg.type === "error"
                  ? "bg-rose-100 text-rose-700"
                  : "bg-emerald-100 text-emerald-800"
              }`}
            >
              {parseMsg.type === "error" ? (
                <AlertCircle className="w-4 h-4" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
              {parseMsg.text}
            </div>
          )}

          <button
            type="button"
            onClick={handleSmartParse}
            disabled={isParsing || (!invoiceText.trim() && !file)}
            className="w-full bg-amber-500 text-white font-bold py-2.5 rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 text-sm"
          >
            {isParsing ? "Extracting..." : "Extract Items"}
          </button>

          {!!parsedItems.length && (
            <div className="bg-white rounded-xl border border-amber-200 overflow-hidden">
              <div className="px-3 py-2 border-b border-amber-100 bg-amber-50 flex items-center justify-between gap-2">
                <span className="text-xs font-bold text-amber-900 uppercase tracking-wide">
                  Parsed Items ({parsedItems.length})
                </span>
                <button
                  type="button"
                  onClick={() => setParsedFullscreen(true)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold rounded-lg bg-white text-amber-900 border border-amber-200 hover:bg-amber-100"
                >
                  <Maximize2 className="w-3.5 h-3.5" /> Full Screen
                </button>
              </div>
              {renderParsedRows("max-h-[340px]")}
            </div>
          )}
        </div>
      )}

      {parsedFullscreen && (
        <div className="fixed inset-0 z-[70] bg-slate-900/40 p-3 md:p-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl h-full flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
              <span className="text-sm font-bold text-slate-800">
                Parsed Items ({parsedItems.length})
              </span>
              <button
                type="button"
                onClick={() => setParsedFullscreen(false)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"
              >
                <Minimize2 className="w-3.5 h-3.5" /> Exit Full Screen
              </button>
            </div>
            <div className="flex-1 overflow-hidden">{renderParsedRows("h-full")}</div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">
            Type
          </label>
          {!newType.adding ? (
            <div className="flex gap-2">
              <select
                name="type"
                value={form.type}
                onChange={handleChange}
                className="w-full text-sm p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
              >
                {Object.entries(types).map(([k, c]) => (
                  <option key={k} value={k}>
                    {c.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() =>
                  setNewType({ adding: true, value: "", behavior: "outflow" })
                }
                className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-200 whitespace-nowrap"
              >
                + New
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200">
              <input
                autoFocus
                type="text"
                value={newType.value}
                onChange={(e) =>
                  setNewType((p) => ({ ...p, value: e.target.value }))
                }
                placeholder="Type name (e.g. Retention)"
                className="w-full text-sm p-2 border border-slate-200 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
              />
              <select
                value={newType.behavior}
                onChange={(e) =>
                  setNewType((p) => ({ ...p, behavior: e.target.value }))
                }
                className="w-full text-sm p-2 border border-slate-200 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
              >
                <option value="outflow">Money Going Out (-)</option>
                <option value="inflow">Money Coming In (+)</option>
                <option value="liability">Loan / Liability (-)</option>
              </select>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleAddType}
                  className="flex-1 py-1.5 bg-indigo-600 text-white rounded-md text-xs font-bold hover:bg-indigo-700"
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setNewType({ adding: false, value: "", behavior: "outflow" })
                  }
                  className="flex-1 py-1.5 bg-slate-200 text-slate-700 rounded-md text-xs font-bold"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">
            Date
          </label>
          <input
            type="date"
            name="date"
            value={form.date}
            onChange={handleChange}
            required
            className="w-full text-sm p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">
            Category
          </label>
          {!newCat.adding ? (
            <div className="flex gap-2">
              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                className="w-full text-sm p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
              >
                {(cats[form.type] || ["General"]).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => {
                  setNewCat({ adding: true, value: "" });
                  setNewCatError("");
                }}
                className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-200 whitespace-nowrap"
              >
                + New
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
                <input
                  autoFocus
                  type="text"
                  value={newCat.value}
                  onChange={(e) => {
                    setNewCat((p) => ({ ...p, value: e.target.value }));
                    if (newCatError) setNewCatError("");
                  }}
                  placeholder="New category..."
                  className="w-full text-sm p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
                />
              <button
                type="button"
                onClick={handleAddCat}
                className="px-4 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-bold hover:bg-indigo-200"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => {
                  setNewCat({ adding: false, value: "" });
                  setNewCatError("");
                }}
                className="px-3 bg-slate-100 text-slate-600 rounded-lg text-sm font-bold hover:bg-slate-200"
              >
                x
              </button>
            </div>
          )}
          {!!newCatError && (
            <p className="mt-2 text-xs font-semibold text-rose-600">
              {newCatError}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">
            Payee / Vendor
          </label>
          <input
            type="text"
            name="payee"
            value={form.payee}
            onChange={handleChange}
            required
            placeholder="e.g. Dangote Cement, FCDA"
            className="w-full text-sm p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">
            Amount
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <span className="text-slate-400 font-medium">NGN</span>
            </div>
            <input
              type="number"
              step="0.01"
              min="0"
              name="amount"
              value={form.amount}
              onChange={handleChange}
              required
              placeholder="0.00"
              className="w-full text-sm pl-14 p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white font-mono"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">
            Notes / Ref #
          </label>
          <input
            type="text"
            name="notes"
            value={form.notes}
            onChange={handleChange}
            placeholder="Invoice # or description"
            className="w-full text-sm p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="submit"
            className="flex-1 bg-indigo-600 text-white font-bold py-3 rounded-xl hover:bg-indigo-700 transition-colors shadow-sm text-sm"
          >
            {isEditing ? "Update Transaction" : "Record Transaction"}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={onCancelEdit}
              className="px-4 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 text-sm"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function normalizeType(rawType, availableTypes = DEFAULT_TRANSACTION_TYPES) {
  const value = String(rawType || "").toLowerCase().trim();
  if (!value) return "expense";
  if (availableTypes[value]) return value;
  if (value.includes("liab")) return normalizeTransactionTypeKey("liability", availableTypes);
  if (["disbursement", "inflow", "revenue", "income", "funding", "loan_funding"].includes(value)) {
    return normalizeTransactionTypeKey("income", availableTypes);
  }
  return normalizeTransactionTypeKey(value, availableTypes);
}

function detectCategoryDirection(categoryName) {
  const normalized = String(categoryName || "").toLowerCase();
  if (!normalized.trim()) return null;

  const inflowHints = [
    "sale",
    "sales",
    "revenue",
    "income",
    "earn",
    "receipt",
    "receivable",
    "refund",
    "profit",
    "gain",
    "commission",
    "dividend",
    "interest income",
  ];
  const outflowHints = [
    "expense",
    "cost",
    "purchase",
    "payroll",
    "salary",
    "rent",
    "utility",
    "bill",
    "fee",
    "tax",
    "payment",
    "repayment",
    "debt",
    "loan repayment",
    "vendor",
  ];

  if (inflowHints.some((hint) => normalized.includes(hint))) return "inflow";
  if (outflowHints.some((hint) => normalized.includes(hint))) return "outflow";
  return null;
}

function isCategoryDirectionCompatible(direction, typeBehavior) {
  if (!direction) return true;
  const behavior = String(typeBehavior || "").toLowerCase();
  if (behavior === "liability") return direction === "outflow";
  return behavior === direction;
}

function formatDirectionLabel(direction) {
  if (direction === "inflow") return "money coming in (+)";
  if (direction === "outflow" || direction === "liability") {
    return "money going out (-)";
  }
  return "unknown direction";
}

function normalizeAmount(value) {
  const n = parseFloat(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? String(n) : "";
}

function normalizeDate(value) {
  const today = new Date().toISOString().split("T")[0];
  if (!value) return today;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return today;
  return d.toISOString().split("T")[0];
}
